/**
 * escposPrinter — direct thermal printing (ESC/POS raster) over WebUSB, or over
 * Web Bluetooth via bluetoothPrinter.js — same bytes, the device's printer
 * settings pick the pipe.
 *
 * Why this exists: the macOS CUPS / POS-80 print path feeds a fixed ~7cm blank
 * tail before the auto-cut that the exposed driver options can't trim (verified
 * on-device: a raw `lp` text print tails the same ~7cm, so it's the driver, not
 * our docDef). WebUSB lets us send the cut command (GS V) immediately after the
 * content, so the only trailing blank left is the printer's physical
 * head-to-cutter gap (~1–1.5cm).
 *
 * Scope / limits:
 *  - Browser support is Chromium desktop + Chrome Android only. Safari & iOS have
 *    NO WebUSB. Callers MUST feature-detect with isSupported() and fall back to
 *    the existing PDF print path when it returns false (or when printing throws —
 *    e.g. macOS/CUPS still holds the USB interface and claimInterface() fails).
 *  - isSupported() only turns true on a device whose printer settings say USB or
 *    Bluetooth (utils/printerSettings.js, set in the printer settings offcanvas) —
 *    a waiter's phone is set to "no printer" and keeps the PDF/skip behaviour.
 *  - Thai text is drawn to a <canvas> by the browser (perfect Sarabun/Thai
 *    glyphs) and shipped as a 1-bit raster. We never use ESC/POS text mode or
 *    Thai codepages, which are unreliable across POS-80 firmware.
 *
 * Hardware: NEO 8300 / POS-80, 203dpi, 72mm printable = 576 dots wide.
 */

import { createReportPdf } from '@/utils/pdfReport';
import { printerSettings, hasCashDrawer, guessPaperWidth, savePrinterSettings } from '@/utils/printerSettings';
import { canUseBluetooth, openBluetooth, writeBluetooth } from '@/utils/bluetoothPrinter';

export const PRINTER_DOTS = 576; // 72mm printable @ 203dpi (8 dots/mm)

// Map a paper width setting (the device's, see printerPaperWidth()) to printable
// raster width in dots. 58mm-class printers print 384 dots; 72mm/80mm-class
// print 576 (POS-80's printable area is 72mm — see thermal-printer-setup.md).
// Unknown → 576.
export function dotsForPaperWidth(width) {
  return width === '58mm' ? 384 : PRINTER_DOTS;
}

const ESC = 0x1b;
const GS = 0x1d;

/**
 * Luminance cut-off for the 1-bit raster (0 = black, 255 = white).
 *
 * pdf.js/canvas draw glyphs anti-aliased, so a 6–7pt Thai stroke is mostly
 * mid-grey edge pixels with only a hairline of true black. A textbook 128 cut
 * threw those edges away and the slip came out thin and faded — the head heat
 * was never the problem. 176 keeps the grey edge, which fattens every stroke by
 * roughly a dot: darker, bolder, same paper length.
 *
 * Don't push much past ~190: at 6pt the Thai vowel/tone marks start merging
 * into the glyph below them, and the PromptPay/review QR modules bloat enough
 * to eat their quiet zone.
 */
const INK_THRESHOLD = 176;

/**
 * Does this browser have WebUSB at all? Gates the USB option on the printer
 * settings page (iOS never does — WebKit has no WebUSB).
 */
export function canPair() {
  if (typeof navigator === 'undefined' || !navigator.usb) return false;
  return !/iPhone|iPad|iPod/i.test(navigator.userAgent); // iOS/Safari: no WebUSB
}

/**
 * Is this device a printing station? Gates every print/cash-drawer path.
 *
 * Read from the device's printer settings, not guessed from the user agent: only
 * a device set up to print over USB or Bluetooth counts. A waiter's phone is set
 * to "no printer" (or never set up), so it keeps falling through to the PDF/skip
 * path instead of throwing a chooser or a failed-print toast after every payment.
 * printerSettings is reactive, so computeds calling this re-evaluate — the print
 * buttons light up as soon as setup is saved, without a reload.
 */
export function isSupported() {
  if (printerSettings.transport === 'usb') return canPair();
  if (printerSettings.transport === 'bluetooth') return canUseBluetooth();
  return false;
}

// ---- USB device handling -------------------------------------------------

function findOutEndpoint(device) {
  for (const cfg of device.configurations) {
    for (const intf of cfg.interfaces) {
      for (const alt of intf.alternates) {
        const ep = alt.endpoints.find(e => e.direction === 'out' && e.type === 'bulk');
        if (ep) return { interfaceNumber: intf.interfaceNumber, endpointNumber: ep.endpointNumber };
      }
    }
  }
  return null;
}

// Remember which physical printer was chosen (by USB serial) so we keep using the
// SAME one across reloads / when several printers are paired — not just whatever
// getDevices() lists first. Survives a printer swap: pick the new one once and its
// serial replaces the old.
const SERIAL_KEY = 'escpos_printer_serial';
function getStoredSerial() {
  try { return localStorage.getItem(SERIAL_KEY) || ''; } catch (e) { return ''; }
}
function setStoredSerial(serial) {
  try { if (serial) localStorage.setItem(SERIAL_KEY, serial); } catch (e) { /* ignore */ }
}

// Show ALL USB devices in the chooser, not just classCode 7. Many thermal
// printers report their printer class only at the interface level (bDeviceClass
// 0), which the classCode filter misses → "No compatible devices found". The
// user picks the right device in the chooser regardless.
const PRINTER_FILTERS = { filters: [] };

// A previously-granted, currently-connected printer: prefer the stored serial,
// else the first one. getDevices() never returns unplugged devices, so a dead
// printer that was removed simply won't appear here.
async function pickGrantedDevice() {
  const granted = await navigator.usb.getDevices();
  if (!granted || !granted.length) return null;
  const serial = getStoredSerial();
  return (serial && granted.find(d => d.serialNumber === serial)) || granted[0];
}

/**
 * The printer this device will print to (granted + currently plugged in), or
 * null. For the settings page's status line — printing itself goes through
 * openPrinter().
 */
export async function getPairedPrinter() {
  if (!canPair()) return null;
  try {
    return await pickGrantedDevice();
  } catch (e) {
    return null;
  }
}

/**
 * Call `cb` whenever a USB device is plugged in or out, so the settings page's
 * status line follows the cable. Returns the unsubscribe function.
 */
export function onPrinterConnectionChange(cb) {
  if (!canPair()) return () => {};
  navigator.usb.addEventListener('connect', cb);
  navigator.usb.addEventListener('disconnect', cb);
  return () => {
    navigator.usb.removeEventListener('connect', cb);
    navigator.usb.removeEventListener('disconnect', cb);
  };
}

async function openAndClaim(device) {
  if (!device.opened) await device.open();
  if (device.configuration === null) await device.selectConfiguration(1);
  const ep = findOutEndpoint(device);
  if (!ep) throw new Error('No bulk OUT endpoint on USB printer');
  await device.claimInterface(ep.interfaceNumber);
  return { device, ep };
}

/** Human-readable printer name, e.g. "Artery CMD:ESCPO POS80 Printer USB". */
export function printerDisplayName(device) {
  if (!device) return '';
  return [device.manufacturerName, device.productName].filter(Boolean).join(' ') || 'USB';
}

// A printer was just picked in a chooser: follow a swap to a different paper
// class (80mm → 58mm unit) by reading the size out of the model name. Only
// touches settings when the device is already set up for that same transport —
// the settings offcanvas does its own save when it's the one pairing.
function adoptPaperWidth(name, transport) {
  const guessed = guessPaperWidth(name);
  if (guessed && printerSettings.transport === transport && guessed !== printerSettings.paperWidth) {
    savePrinterSettings({ paperWidth: guessed });
  }
}

function adoptPrinter(device) {
  setStoredSerial(device.serialNumber || '');
  adoptPaperWidth(printerDisplayName(device), 'usb');
}

/**
 * Show the USB chooser so the user can (re)pick a printer — wired to the
 * printer settings offcanvas ("Find printer"). Must run from a user gesture.
 * Returns the USBDevice.
 */
export async function selectPrinter() {
  const device = await navigator.usb.requestDevice(PRINTER_FILTERS);
  adoptPrinter(device);
  return device;
}

// Resolve a usable printer for a print job. Tries the remembered/granted device
// first; if it can't be opened or claimed (dead unit, or OS/CUPS holds it) it
// pops the chooser so the user can pick another — rather than silently failing
// to PDF. requestDevice() stays within the print click's activation window.
//
// promptIfMissing:false is for prints nobody clicked (auto-print after payment,
// the standalone cash-drawer kick). Those must never raise a device chooser: it
// would land on the cashier mid-shift, and by then the click's user-activation
// window has usually expired anyway, so it would just throw a confusing error.
async function openPrinter({ promptIfMissing = true } = {}) {
  const granted = await pickGrantedDevice();
  if (granted) {
    try {
      return await openAndClaim(granted);
    } catch (e) {
      // fall through to an explicit pick
    }
  }
  if (!promptIfMissing) throw new Error('No paired USB printer on this device');
  const picked = await navigator.usb.requestDevice(PRINTER_FILTERS);
  // A swap to a different paper class takes effect from the NEXT print — this
  // job's canvas was already rendered at the old width.
  adoptPrinter(picked);
  return await openAndClaim(picked);
}

// ---- Raster encoding -----------------------------------------------------

// Canvas RGBA → ESC/POS GS v 0 raster, emitted in horizontal bands to stay
// under the printer's image buffer limit.
//
// feedLines defaults to 0: GS V B already feeds the paper to the cutter, so every
// extra ESC d line is pure blank tail on top of the printer's unavoidable
// head-to-cutter gap (~1–1.5cm). The QR slips have always printed this way — bills
// just never caught up, which is where their extra ~7mm of footer came from.
function buildPrintData(canvas, { feedLines = 0, cut = true, cashDrawer = false } = {}) {
  const w = canvas.width;
  const h = canvas.height;
  const ctx = canvas.getContext('2d');
  const px = ctx.getImageData(0, 0, w, h).data;
  const bytesPerRow = Math.ceil(w / 8);

  const mono = new Uint8Array(bytesPerRow * h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      const alpha = px[i + 3];
      const lum = alpha === 0 ? 255 : px[i] * 0.299 + px[i + 1] * 0.587 + px[i + 2] * 0.114;
      if (lum < INK_THRESHOLD) mono[y * bytesPerRow + (x >> 3)] |= 0x80 >> (x & 7);
    }
  }

  const parts = [];
  parts.push(new Uint8Array([ESC, 0x40])); // ESC @ — reset
  // ESC p 0 25 250 — fire the cash-drawer kick pulse (drawer plugged into the
  // printer's RJ11 port). Sent with the print job so cash payments open it.
  
  if (cashDrawer) parts.push(new Uint8Array([ESC, 0x70, 0x00, 0x19, 0xFA]));
  const BAND = 128;
  for (let y = 0; y < h; y += BAND) {
    const rows = Math.min(BAND, h - y);
    parts.push(new Uint8Array([
      GS, 0x76, 0x30, 0x00,                       // GS v 0, mode 0
      bytesPerRow & 0xff, (bytesPerRow >> 8) & 0xff,
      rows & 0xff, (rows >> 8) & 0xff
    ]));
    parts.push(mono.subarray(y * bytesPerRow, (y + rows) * bytesPerRow));
  }
  if (feedLines > 0) parts.push(new Uint8Array([ESC, 0x64, feedLines & 0xff])); // ESC d n
  if (cut) parts.push(new Uint8Array([GS, 0x56, 0x42, 0x00])); // GS V B 0 — feed to cutter + partial cut

  let len = 0;
  parts.forEach(p => { len += p.length; });
  const out = new Uint8Array(len);
  let off = 0;
  parts.forEach(p => { out.set(p, off); off += p.length; });
  return out;
}

// Open whichever pipe this device is set up for → { write(bytes), close() }.
// Bluetooth stays connected between jobs (a GATT connect costs 1–3s); USB claims
// and releases the interface per job so macOS/CUPS can still use the printer.
async function openTransport(opts) {
  if (printerSettings.transport === 'bluetooth') {
    const { picked } = await openBluetooth(opts);
    // A swap to a different paper class takes effect from the NEXT print — this
    // job's canvas was already rendered at the old width.
    if (picked) adoptPaperWidth(picked.name, 'bluetooth');
    return { write: (data) => writeBluetooth(data, printerSettings.btSpeed), close: async () => {} };
  }
  const { device, ep } = await openPrinter(opts);
  return {
    write: async (data) => {
      const started = Date.now();
      const CHUNK = 8 * 1024;
      for (let i = 0; i < data.length; i += CHUNK) {
        await device.transferOut(ep.endpointNumber, data.subarray(i, i + CHUNK));
      }
      return { bytes: data.length, ms: Date.now() - started };
    },
    close: async () => {
      try { await device.releaseInterface(ep.interfaceNumber); } catch (e) { /* ignore */ }
    },
  };
}

/**
 * Send a canvas to the thermal printer and cut. Resolves { bytes, ms } (the
 * settings test print shows the throughput); rejects on any USB/Bluetooth
 * failure so the caller can fall back to the PDF path.
 *
 * opts.cashDrawer says "this print is for a cash payment"; whether a drawer
 * actually gets kicked is the device's call (printer settings), since the
 * drawer is cabled to one specific printer.
 */
export async function printCanvas(canvas, opts = {}) {
  const conn = await openTransport(opts);
  try {
    return await conn.write(buildPrintData(canvas, { ...opts, cashDrawer: opts.cashDrawer === true && hasCashDrawer() }));
  } finally {
    await conn.close();
  }
}

/**
 * Open the cash drawer (kick pulse) without printing. Resolves false without
 * touching the printer when this device is set up without a drawer; rejects on
 * USB/Bluetooth failure.
 */
export async function openCashDrawer(opts = {}) {
  if (!isSupported() || !hasCashDrawer()) return false;
  const conn = await openTransport(opts);
  try {
    await conn.write(new Uint8Array([ESC, 0x40, ESC, 0x70, 0x00, 0x19, 0xFA]));
  } finally {
    await conn.close();
  }
  return true;
}

// ---- Slip canvas rendering ----------------------------------------------

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

async function ensureFont() {
  if (typeof document === 'undefined' || !document.fonts) return;
  try {
    await Promise.all([
      document.fonts.load('bold 44px Sarabun'),
      document.fonts.load('26px Sarabun')
    ]);
  } catch (e) { /* fall back to system Thai font */ }
}

// Word-wrap `text` to fit `maxW` px under the ctx's current font. Splits on
// whitespace; a single unbreakable token (e.g. Thai with no spaces) stays on its
// own line and may exceed maxW — the caller's small fonts keep that rare.
function wrapTextLines(ctx, text, maxW) {
  const words = String(text || '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  const lines = [];
  let cur = words[0];
  for (let i = 1; i < words.length; i++) {
    const test = cur + ' ' + words[i];
    if (ctx.measureText(test).width <= maxW) cur = test;
    else { lines.push(cur); cur = words[i]; }
  }
  lines.push(cur);
  return lines;
}

/**
 * Render the QR service slip as a compact **horizontal** layout — QR on the left,
 * heading + subtitle stacked (and vertically centred) on the right — sized to the
 * chosen paper width (default 576px = 72mm). This roughly halves the slip length
 * vs the old stacked layout (the QR no longer adds its full height on top of the
 * text). Everything scales with widthPx (e.g. dotsForPaperWidth(printerPaperWidth()))
 * so 58mm slips shrink proportionally. The canvas is sized exactly to its content
 * so the WebUSB cut lands right under it.
 */
export async function renderQrSlipCanvas({ heading = '', subtitle = '', qrDataUrl = '', widthPx = PRINTER_DOTS }) {
  await ensureFont();
  const qrImg = qrDataUrl ? await loadImage(qrDataUrl) : null;

  const W = widthPx;
  const r = W / PRINTER_DOTS; // scale every dimension to the chosen paper width
  const padX = Math.round(18 * r);
  const padY = Math.round(8 * r);           // tight top/bottom — hardware adds its own tail
  const qrSize = Math.round(256 * r);       // ~32mm — plenty for scanning, was 384
  const gap = Math.round(22 * r);           // QR ↔ text column
  const headingPx = Math.round(34 * r);
  const subPx = Math.round(24 * r);
  // Thai stacks vowels/tone marks above & below the baseline, so it needs a
  // taller line box than Latin — 1.35 keeps marks (เ◌ื่, ◌ุ) from clipping.
  const headingLH = Math.round(headingPx * 1.35);
  const subLH = Math.round(subPx * 1.35);

  const textX = padX + (qrImg ? qrSize + gap : 0);
  const textW = Math.max(1, W - textX - padX);

  const canvas = document.createElement('canvas');
  canvas.width = W;
  const ctx = canvas.getContext('2d');

  // Measure/wrap first (measureText is valid before the canvas is sized).
  ctx.font = `bold ${headingPx}px Sarabun, sans-serif`;
  const headingLines = wrapTextLines(ctx, heading, textW);
  ctx.font = `${subPx}px Sarabun, sans-serif`;
  const subLines = wrapTextLines(ctx, subtitle, textW);

  // Separator rule between heading and subtitle, with its own clearance so a Thai
  // subtitle's upper marks (เ◌ื่) don't ride into the line and the heading's
  // descenders don't touch it from above.
  const hasSep = headingLines.length && subLines.length;
  const sepThick = Math.max(1, Math.round(1.5 * r));
  const gapAboveSep = Math.round(9 * r);
  const gapBelowSep = Math.round(13 * r);   // extra room for Thai upper marks
  const sepBlock = hasSep ? (gapAboveSep + sepThick + gapBelowSep) : 0;
  const textBlockH = headingLines.length * headingLH + sepBlock + subLines.length * subLH;
  const contentH = Math.max(qrImg ? qrSize : 0, textBlockH);
  canvas.height = padY * 2 + contentH; // resizing resets ctx state → re-init below

  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, W, canvas.height);
  ctx.fillStyle = '#000';
  ctx.textBaseline = 'top';

  // QR — left, vertically centred against the content block.
  if (qrImg) {
    const qrY = padY + Math.round((contentH - qrSize) / 2);
    ctx.drawImage(qrImg, padX, qrY, qrSize, qrSize);
  }

  // Text — right column, vertically centred, left-aligned next to the QR.
  ctx.textAlign = 'left';
  let ty = padY + Math.round((contentH - textBlockH) / 2);
  ctx.font = `bold ${headingPx}px Sarabun, sans-serif`;
  for (const line of headingLines) { ctx.fillText(line, textX, ty); ty += headingLH; }
  if (hasSep) {
    ty += gapAboveSep;
    ctx.fillRect(textX, ty, Math.round(textW * 0.55), sepThick);
    ty += sepThick + gapBelowSep;
  }
  ctx.font = `${subPx}px Sarabun, sans-serif`;
  for (const line of subLines) { ctx.fillText(line, textX, ty); ty += subLH; }

  return canvas;
}

// QR-like block: ~50% dark 8-dot modules — a real receipt's PromptPay/review QR
// is the densest thing it prints, and dense rows are what slow the print head.
function drawQrLike(ctx, x0, y0, size) {
  const cell = 8;
  const n = Math.floor(size / cell);
  let seed = 7;
  for (let r = 0; r < n; r++) {
    for (let c = 0; c < n; c++) {
      seed = (seed * 1103515245 + 12345) & 0x7fffffff;
      if (seed & 0x10000) ctx.fillRect(x0 + c * cell, y0 + r * cell, cell, cell);
    }
  }
}

/**
 * Test slip for the printer settings page. The border runs the full raster
 * width, so a wrong paper-width setting shows at a glance: 80mm set on a 58mm
 * printer loses its right edge, 58mm on an 80mm printer leaves a blank strip.
 * Font sizes stay fixed (not scaled by width) — same rule as the real receipt.
 *
 * dense: adds two QR-sized ~50%-black blocks and a solid bar before `footer`.
 * A thermal head slows down on dark rows, so the printer drains Bluetooth data
 * slower there — a text-only test slip passed every speed on-device while real
 * receipts (with QRs) jammed at unpaced sending. The long test must be at least
 * as dark as a real receipt, and `footer` is the last line so "it printed all
 * the way to the end" is checkable on paper.
 */
export async function renderTestSlipCanvas({ heading = '', lines = [], widthPx = PRINTER_DOTS, dense = false, footer = '' }) {
  await ensureFont();
  const W = widthPx;
  const border = 3;
  const pad = 18;
  const headingPx = 32;
  const linePx = 24;
  const headingLH = Math.round(headingPx * 1.35); // room for Thai upper/lower marks
  const lineLH = Math.round(linePx * 1.35);
  const block = Math.min(320, W - pad * 2); // ≈ the receipt's review QR at 80mm
  const bar = 48;
  const denseH = dense ? (block + 12) * 2 + bar + 12 : 0;
  const footerH = footer ? lineLH + 6 : 0;

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = pad * 2 + headingLH + 8 + lines.length * lineLH + denseH + footerH;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, W, canvas.height);
  ctx.fillStyle = '#000';
  ctx.strokeStyle = '#000';
  ctx.lineWidth = border;
  ctx.strokeRect(border / 2, border / 2, W - border, canvas.height - border);

  ctx.textBaseline = 'top';
  let y = pad;
  ctx.font = `bold ${headingPx}px Sarabun, sans-serif`;
  ctx.fillText(heading, pad, y);
  y += headingLH + 8;
  ctx.font = `${linePx}px Sarabun, sans-serif`;
  for (const line of lines) { ctx.fillText(line, pad, y); y += lineLH; }
  if (dense) {
    y += 6;
    drawQrLike(ctx, pad, y, block);
    y += block + 12;
    ctx.fillRect(pad, y, W - pad * 2, bar);
    y += bar + 12;
    drawQrLike(ctx, pad, y, block);
    y += block + 6;
  }
  if (footer) {
    ctx.font = `bold ${linePx}px Sarabun, sans-serif`;
    ctx.fillText(footer, pad, y + 6);
  }
  return canvas;
}

// ---- pdfmake docDef → canvas (for the complex bill receipt) --------------

// pdf.js is heavy and only needed for WebUSB bill printing, so load it lazily.
// The worker is served same-origin (copied by CopyWebpackPlugin in vue.config.js).
let _pdfjs = null;
async function getPdfjs() {
  if (_pdfjs) return _pdfjs;
  const mod = await import(/* webpackChunkName: "pdfjs" */ 'pdfjs-dist/legacy/build/pdf');
  // The legacy build is UMD — under webpack's interop the API may sit on the
  // module namespace OR on `.default`. Pick whichever actually has getDocument.
  const pdfjs = mod.getDocument ? mod : (mod.default || mod);
  pdfjs.GlobalWorkerOptions.workerSrc = (process.env.BASE_URL || '/') + 'pdf.worker.min.js';
  _pdfjs = pdfjs;
  return pdfjs;
}

/**
 * Render a pdfmake doc definition to a single canvas at printer width, so the
 * bill receipt reuses its existing pdfmake layout (tax/discount/loyalty/QR)
 * instead of re-drawing it. Multi-page docs are stacked vertically.
 * Build the docDef with its short-page padding OFF — WebUSB cuts exactly.
 */
export async function renderDocDefToCanvas(docDef, { widthPx = PRINTER_DOTS } = {}) {
  const pdfjs = await getPdfjs();
  const blob = await createReportPdf(docDef).getBlob();
  const data = new Uint8Array(await blob.arrayBuffer());
  const pdf = await pdfjs.getDocument({ data }).promise;

  const pages = [];
  let totalH = 0;
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const scale = widthPx / page.getViewport({ scale: 1 }).width;
    const viewport = page.getViewport({ scale });
    const c = document.createElement('canvas');
    c.width = Math.round(viewport.width);
    c.height = Math.round(viewport.height);
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, c.width, c.height);
    await page.render({ canvasContext: ctx, viewport }).promise;
    pages.push(c);
    totalH += c.height;
  }

  if (pages.length === 1) return pages[0];
  const out = document.createElement('canvas');
  out.width = widthPx;
  out.height = totalH;
  const octx = out.getContext('2d');
  octx.fillStyle = '#fff';
  octx.fillRect(0, 0, out.width, out.height);
  let y = 0;
  for (const c of pages) { octx.drawImage(c, 0, y); y += c.height; }
  return out;
}
